import {
  Analytics,
  type Partner,
  type Product
} from '@mzc-watchover/adminapp-appsync'
import {
  compact,
  filter,
  find,
  includes,
  isEmpty,
  isNil,
  map,
  size,
  slice,
  sortBy,
  startsWith,
  toNumber
} from 'lodash/fp'
import log from 'loglevel'
import React from 'react'
import {
  CheckboxGroupInput,
  Form,
  Loading,
  RadioButtonGroupInput,
  TextInput,
  useTranslate
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography
} from '@mui/material'
import { useGetPartners } from 'components/DataProvider/hooks'

log.setLevel(log.levels.DEBUG)

const productChoices = [
  { id: Analytics.activity, name: 'テラシテR' },
  { id: 'solar', name: 'テラシテS' },
  { id: Analytics.disasterInfoOnly, name: '安否共有' },
  { id: 'external', name: '外部サービス' }
]

const PartnerSelectContent: React.FC<{
  setPartners: (partners: Array<Partial<Partner>>) => void
  partners: Array<Partial<Partner>> | undefined
}> = ({ setPartners, partners }) => {
  const translate = useTranslate()
  const [page, setPage] = React.useState<number>(1)
  const [choices, setChoices] = React.useState<string[]>([
    Analytics.activity,
    Analytics.disasterInfoOnly,
    'solar',
    'external'
  ])
  const [total, setTotal] = React.useState(0)
  const { getValues } = useFormContext()
  const { data, isLoading } = useGetPartners()
  const onPrev = (): void => {
    setPage(page - 1)
  }
  const onNext = (): void => {
    setPage(page + 1)
  }
  const onChoice = (): void => {
    const { products } = getValues()
    setChoices(products)
    setPage(1)
  }
  const filterValue = useWatch({ name: 'filter' })
  React.useEffect(() => {
    if (isLoading) return
    const filteredPartners = filter(
      (item: Partial<Partner>) =>
        !isNil(
          find(
            (product: Partial<Product>) =>
              !isNil(
                find((choice: string) =>
                  startsWith(choice)(product.analytics as string)
                )(choices)
              )
          )(item.products)
        )
    )(data)
    if (isNil(filterValue) || isEmpty(filterValue) || size(filterValue) <= 2) {
      setTotal(size(filteredPartners))
      setPartners(slice((page - 1) * 10, page * 10)(filteredPartners))
    } else {
      const partners = filter((item: { name: string }) =>
        includes(filterValue)(item.name)
      )(filteredPartners)
      setTotal(size(partners))
      setPartners(
        slice((page - 1) * 10, page * 10)(partners) as Array<Partial<Partner>>
      )
    }
  }, [isLoading, data, page, filterValue, choices])
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: 'center'
      }}
      spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Typography variant="h6">
          {translate('resources.partnerSelect.dialog.title')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <CheckboxGroupInput
          source="products"
          choices={productChoices}
          label={false}
          defaultValue={[
            Analytics.activity,
            Analytics.disasterInfoOnly,
            'solar',
            'external'
          ]}
          onChange={onChoice}
        />
      </Grid>
      <Grid item xs={12}>
        {isLoading || isNil(partners) ? (
          !isEmpty(choices) ? (
            <Loading />
          ) : (
            <></>
          )
        ) : (
          <RadioButtonGroupInput
            source="partner"
            choices={partners}
            row={false}
            label={false}
          />
        )}
      </Grid>
      <Grid item>
        <Button variant="contained" onClick={onPrev} disabled={page === 1}>
          {translate('resources.partnerSelect.dialog.buttons.prev')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={isNil(total) || page >= total / 10}>
          {translate('resources.partnerSelect.dialog.buttons.next')}
        </Button>
      </Grid>
    </Grid>
  )
}
const OrganizationSelectContent: React.FC<{
  partner: Partial<Partner>
}> = ({ partner }) => {
  const translate = useTranslate()
  const [page, setPage] = React.useState<number>(1)
  const [products, setProducts] = React.useState<
    Array<{ id: string; name: string }>
  >([])
  const filterValue = useWatch({ name: 'filter' })
  React.useEffect(() => {
    const { products } = partner
    setProducts(
      sortBy<{ id: string; name: string }>('name')(
        compact(
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: Partial<Product>, id: number) => {
            const { contactOrganization: name } = item
            if (isNil(name)) return undefined
            if (
              isNil(filterValue) ||
              isEmpty(filterValue) ||
              size(filterValue) <= 2
            ) {
              return { id, name }
            } else {
              return includes(filterValue)(name) ? { id, name } : undefined
            }
          })(products)
        )
      )
    )
  }, [partner, filterValue])
  const total = size(products)
  const onPrev = (): void => {
    setPage(page - 1)
  }
  const onNext = (): void => {
    setPage(page + 1)
  }
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: 'center'
      }}
      spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Typography variant="h6">
          {translate('resources.partnerSelect.dialog.selectOrganizationTitle')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <RadioButtonGroupInput
          source="product"
          choices={slice((page - 1) * 10, page * 10)(products)}
          row={false}
          label={false}
        />
      </Grid>
      <Grid item>
        <Button variant="contained" onClick={onPrev} disabled={page === 1}>
          {translate('resources.partnerSelect.dialog.buttons.prev')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={isNil(total) || page >= total / 10}>
          {translate('resources.partnerSelect.dialog.buttons.next')}
        </Button>
      </Grid>
    </Grid>
  )
}
const PartnerSelectDialogContent: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  setProducts: (products: Product[]) => void
  setPartnerName: (name: string) => void
  setPartner: ((partner: Partial<Partner>) => void) | undefined
}> = ({ open, setOpen, setProducts, setPartnerName, setPartner }) => {
  const [selectOrganization, setSelectOrganization] = React.useState<
    Partial<Partner> | undefined
  >(undefined)
  const translate = useTranslate()
  const [partners, setPartners] = React.useState<
    Array<Partial<Partner>> | undefined
  >(undefined)
  const { getValues, setValue } = useFormContext()
  const { partner, product } = getValues()
  // const onClose = (): void => {
  //   setOpen(false)
  // }
  const onConfirm = (): void => {
    if (isNil(selectOrganization)) {
      const item = find((item: Partial<Partner>) => item.id === partner)(
        partners
      )
      if (isNil(item)) return
      const { name, products } = item
      if (!isNil(setPartner)) setPartner(item)
      setPartnerName(`${name as string}さま`)
      setProducts(products as Product[])
      setOpen(false)
    } else {
      const item = find((item: Partial<Partner>) => item.id === partner)(
        partners
      )
      if (isNil(item)) return
      const { id, products } = item
      if (isNil(products) || isNil(id)) return
      const productData = products[toNumber(product)]
      if (isNil(productData)) return
      const { contactOrganization: name } = productData
      if (!isNil(setPartner)) setPartner({ id: `${id}-${product as string}` })
      setPartnerName(`${name as string}さま`)
      setProducts([productData] as Product[])
      setOpen(false)
    }
  }
  const onSelectOrganization = (): void => {
    setValue('filter', null, { shouldDirty: true })
    const item = find((item: Partial<Partner>) => item.id === partner)(partners)
    if (!isNil(item)) {
      setSelectOrganization(item)
    }
  }
  return (
    // <Confirm
    //   isOpen={open}
    //   title={false}
    //   content={
    //     <PartnerSelectContent setPartners={setPartners} partners={partners} />
    //   }
    //   onClose={onClose}
    //   onConfirm={onConfirm}
    //   cancel="resources.partnerSelect.dialog.buttons.cancel"
    //   confirm="resources.partnerSelect.dialog.buttons.confirm"
    // />
    <Dialog open={open}>
      <DialogContent>
        <TextInput
          source="filter"
          fullWidth
          label="resources.partnerSelect.dialog.filter"
        />
        {isNil(selectOrganization) || isNil(partners) ? (
          <PartnerSelectContent setPartners={setPartners} partners={partners} />
        ) : (
          <OrganizationSelectContent partner={selectOrganization} />
        )}
      </DialogContent>
      <DialogActions>
        {/* <Button variant="outlined" onClick={onClose}>
          {translate('resources.partnerSelect.dialog.buttons.cancel')}
        </Button> */}
        {isNil(selectOrganization) && (
          <Button
            variant="contained"
            onClick={onSelectOrganization}
            disabled={isNil(partner)}>
            {translate(
              'resources.partnerSelect.dialog.buttons.selectOrganization'
            )}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isNil(partner)}>
          {translate('resources.partnerSelect.dialog.buttons.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const PartnerSelectDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  setProducts: (products: Product[]) => void
  setPartnerName: (name: string) => void
  setPartner: ((partner: Partial<Partner>) => void) | undefined
}> = ({ open, setOpen, setProducts, setPartnerName, setPartner }) => {
  return (
    <Form
      defaultValues={{
        products: [
          Analytics.activity,
          Analytics.disasterInfoOnly,
          'solar',
          'external'
        ]
      }}>
      <PartnerSelectDialogContent
        open={open}
        setOpen={setOpen}
        setProducts={setProducts}
        setPartnerName={setPartnerName}
        setPartner={setPartner}
      />
    </Form>
  )
}
